import Typography from "@mui/material/Typography";
import React, {useState} from "react";
import classnames from "classnames";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Popover from "@mui/material/Popover";
import DeleteSharpIcon from "@mui/icons-material/DeleteSharp";
import {DealModal} from "../PricingListPage/DealModal";
import {useFetchHook} from "../../hooks/useFetchHook";
import fetcher from "../../fetcher";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import {toast} from "react-toastify";
import {useListDealsStore} from "../../stores/listDealsStore";
import {useModalsStore} from "../../stores/modalsStore";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {useShallow} from "zustand/react/shallow";

export const ListDealCard = ({deal, isSelected}) => {
    const isTabletOrMobile = useMediaQuery({query: mediaQueryForMobile})
    const openModal = useModalsStore((state) => state.openModal)
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [isDeleting, setIsDeleting] = useState(false)

    const [deleteDealResponse, deleteDeal] = useFetchHook(fetcher.deleteDeal)

    const {
        updateDealFromBackend,
        removeDeletedDealFromList,
    } = useListDealsStore(
        useShallow((state) => ({
            removeDeletedDealFromList: state.removeDeletedDealFromList,
            updateDealFromBackend: state.updateDealFromBackend,
        }))
    )

    useDidMountEffect(function updateListAfterDelete() {
        if (deleteDealResponse?.data?.data?.id) {
            removeDeletedDealFromList(deleteDealResponse?.data?.data?.id)
            toast.success("Successfully deleted deal.")
        }
    }, [deleteDealResponse])

    const car_detail = deal.car_detail
    const quote = deal.formatted_quote

    const isLoading = false // updateDealResponse?.loading && dealId() === deal.id
    const isPurchase = deal.is_purchase
    // Cash = a purchase paid in full up front (one pay). Gets the purple body
    // theme and a "CASH" type label instead of the green "PURCHASE".
    const isCash = deal.is_purchase && deal.is_one_pay

    // Mirror the expiry check used in DealHeader / DealContext: a quote is
    // expired the moment its UTC date is <= today's UTC date.
    const expirationDate = quote?.expiration_date
    const isExpired = Boolean(
        expirationDate &&
        new Date(expirationDate).setUTCHours(0, 0, 0, 0) <= new Date().setUTCHours(0, 0, 0, 0)
    )

    const handleClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDelete = (id) => {
        return deleteDeal(id)
    }

    const openCard = () => {
        if (isTabletOrMobile) {
            window.open(`/quotes/${deal.id}`, "_self")
        } else {
            openModal({
                element: DealModal,
                id: "DealModal",
                props: {
                    id: deal.id,
                    onSave: (newDealState) => updateDealFromBackend(newDealState),
                },
            })
        }
    }

    return (
        <div
            onClick={openCard}
            className={classnames('ListDealCard', {
                selected: isSelected,
                isPurchase: isPurchase
            })}
        >
            {/* ── Top: header (panel bg) ────────────────────────── */}
            <div className={'card-header'}>
                <div className={'card-title-row'}>
                    <div className={'card-title-main'}>
                        <div className={'card-title'}>
                            {car_detail.year} {car_detail.make} {car_detail.model}{car_detail.is_new ? '' : ' [Used]'}
                        </div>
                        <div className={'card-spec'}>{car_detail.trim}</div>
                    </div>
                    <div className={'status-meta'}>
                        <div className={classnames('status-dot', {expired: isExpired})}>
                            {isExpired
                                ? <ErrorOutlineIcon className={'status-icon'}/>
                                : <span className={'dot'}/>}
                            <span className={'status-label'}>{isExpired ? 'Expired' : 'Active'}</span>
                        </div>
                        <MoreHorizIcon
                            className={classnames('action-icon', {loader: isDeleting})}
                            onClick={handleClick}
                        />
                    </div>
                </div>

                {quote.lender_name && (
                    <div className={'card-subtitle'}>{quote.lender_name}</div>
                )}

                {deal.description && (
                    <div className={'card-note'}>{deal.description}</div>
                )}
            </div>

            {/* ── Bottom: type-themed body ──────────────────────── */}
            <div className={classnames('card-body', {
                lease: !isPurchase,
                purchase: isPurchase && !isCash,
                cash: isCash,
            })}>
                <div className={'deal-type-row'}>
                    <span className={'deal-type'}>{isCash ? 'CASH' : isPurchase ? 'PURCHASE' : 'LEASE'}</span>
                    {deal.msrp && (
                        <span className={'msrp-label'}>MSRP ${deal.msrp}</span>
                    )}
                </div>

                <div className={'amounts-row'}>
                    <div className={'amount-left'}>
                        <Typography
                            className={classnames('big pay-detail_value', {loading: isLoading})}
                        >
                            {isCash ? 'N/A' : `$${quote.amount_due_at_signing}`}
                        </Typography>
                        <div className={'lbl'}>{isPurchase ? 'Down Payment' : 'Due at Signing'}</div>
                    </div>
                    <div className={'amount-right'}>
                        <Typography
                            className={classnames('big pay-detail_value', {loading: isLoading})}
                        >
                            ${quote.monthly_payment}
                        </Typography>
                        <div className={'lbl'}>{isCash ? 'Payment' : '/ month'}</div>
                    </div>
                </div>

                <div className={'stats-row'}>
                    <div className={'stat'}>
                        <div className={'val'}>{isCash ? 'N/A' : deal.term}</div>
                        <div className={'key'}>MONTHS</div>
                    </div>
                    {!isPurchase && (
                        <div className={'stat'}>
                            <div className={'val'}>{deal.mileage}</div>
                            <div className={'key'}>MILES</div>
                        </div>
                    )}
                    <div className={'stat'}>
                        <div className={'val'}>${deal.profit}</div>
                        <div className={'key'}>PROFIT</div>
                    </div>
                </div>
            </div>

            <Popover
                onClick={(e) => e.stopPropagation()}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
            >
                <Typography
                    sx={{p: 2, fontWeight: 500}}
                    className={'ListDealCard-menu-item'}
                    onClick={() => handleDelete(deal.id)}
                >
                    Delete
                    <DeleteSharpIcon
                        className={classnames('action-icon', {loader: isDeleting})}
                    />
                </Typography>
            </Popover>
        </div>
    )
}
